import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import { DraggableDialog } from '../DraggablePaper/DraggablePaper';
import ModalFormTitle from '../Tables/TableWithModalEdit/modalFormPieces/ModalFormTitle';
import { DialogActionsWrapper } from '../Tables/TableWithModalEdit/modalFormPieces/ModalFormActions/ModalFormActions';
import EmailChips from '../EmailChips/EmailChips';
import WarningConfirmModal from '../WarningConfirmModal';
import { Context as LanguagesContext } from '../../api/languages/context';
import { getDataOptions } from '../../utils/arrayUtils';
import { applyMergeFields, type MergeContext } from '../../utils/mergeFields';
import {
    MAX_ATTACHMENT_COUNT,
    MAX_TOTAL_ATTACHMENT_BYTES,
    formatBytes
} from '../../utils/emailAttachmentLimits';

import MessageEditor from './MessageEditor';
import { displayToast } from '../RTToast';

/** True when the editor HTML holds any visible text — an emptied editor still leaves `<p><br></p>`. */
const hasMessageText = (html: string): boolean =>
    html
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .trim() !== '';

import type { EmailDocumentKey } from '../../api/emailTemplates/documentKeys';

/** What the picker needs — the endpoint returns nothing more. */
interface ComposeTemplate {
    _id: number | string;
    name: string;
    translations: Record<string, { subject?: string; body?: string }>;
    defaultLanguage?: string;
}

export interface EmailComposePayload {
    emails: string[];
    subject: string;
    /** Editor HTML with merge fields already resolved. Empty for the "None" template. */
    body: string;
    /** Language of the body, so the branded wrapper picks the right direction. */
    language?: string;
    attachments: File[];
    /** For the travel-file log, so it records which message went out. */
    templateName?: string;
}

interface Props {
    open: boolean;
    onClose: () => void;
    /**
     * Title-bar context, mirroring the previous system's `#2002148-DROR/DAVID … Pegasus
     * Tours & Travel LTD`. The sender needs to see which booking they are writing about
     * without going back to the screen behind the dialog.
     */
    contextHeader?: string;
    /** Which document is being sent, if any — this is what the picker is filtered by. */
    documentKey?: EmailDocumentKey;
    /** Values for `<Field Name>` substitution when a template is selected. */
    mergeContext?: MergeContext;
    /** Pre-filled, editable — derived from the document's own title. */
    defaultSubject?: string;
    defaultEmails?: string[];
    /** Size of the generated PDF; it counts toward the same limit the attachments do. */
    documentSizeBytes?: number;
    sending?: boolean;
    onSend: (payload: EmailComposePayload) => void;
}

const NONE = '';

/**
 * The send dialog.
 *
 * Modelled on the previous system's: context header, To, From, Subject, a message
 * template picker, the message body, and an attachment picker. Two deliberate
 * departures, both recorded in the change's design notes:
 *
 *  - **From is read-only.** An address typed at send time is not covered by the sending
 *    domain's DKIM signature and will not align for DMARC, so the message is quarantined
 *    or rejected. The old free-text field was a way to send "as finance"; that belongs to
 *    sender configuration, not to a text box.
 *  - **The body is a rich-text editor**, the same one the template page uses. Template
 *    bodies are HTML; a plain textarea would show the sender their own markup as literal
 *    text and let them corrupt it by editing.
 *
 * "None" is the default and stays exactly as it was: no body, and the endpoint sends the
 * bare attachment it always sent.
 */
const EmailComposeDialog: FC<Props> = ({
    open,
    onClose,
    contextHeader,
    documentKey,
    mergeContext,
    defaultSubject = '',
    defaultEmails,
    documentSizeBytes = 0,
    sending = false,
    onSend
}) => {
    const languagesContext = useContext(LanguagesContext);

    const [emails, setEmails] = useState<string[]>(defaultEmails || []);
    const [subject, setSubject] = useState(defaultSubject);
    const [body, setBody] = useState('');
    const [attachments, setAttachments] = useState<File[]>([]);

    const [sender, setSender] = useState<{
        senderAddress: string;
        fromName?: string;
        replyTo?: string;
    } | null>(null);
    const [templates, setTemplates] = useState<ComposeTemplate[]>([]);
    // True when no rule links a template to this document and the server sent
    // the whole library instead.
    const [isTemplatesFallback, setIsTemplatesFallback] = useState(false);
    const [templateId, setTemplateId] = useState<string>(NONE);
    const [languageCode, setLanguageCode] = useState<string>('');
    // The branded email as it will be delivered, rendered by the server.
    const [previewHtml, setPreviewHtml] = useState<string | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    // A template fills subject and body; edits made afterwards are the sender's, and
    // switching template would throw them away. Tracked from the actual edit handlers
    // rather than by comparing text, so re-selecting the same template is not a "change".
    const [dirty, setDirty] = useState(false);
    const [pendingTemplateId, setPendingTemplateId] = useState<string | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);

    // Direction comes from the `languages` row, never guessed from the language's name.
    const languageOptions = useMemo(
        () =>
            getDataOptions(languagesContext.data) as Array<{
                _id: string;
                name: string;
                code?: string;
                direction?: string;
            }>,
        [languagesContext.data]
    );
    useEffect(() => {
        if (open && !languageOptions.length) languagesContext.get({});
    }, [open]); // eslint-disable-line

    const selectedTemplate = useMemo(
        () => templates.find((t) => String(t._id) === templateId),
        [templates, templateId]
    );

    /** Languages this template actually has wording for — an empty tab is not an option. */
    const templateLanguages = useMemo(
        () =>
            Object.entries(selectedTemplate?.translations || {})
                .filter(([, t]) => (t?.subject || '').trim() || (t?.body || '').trim())
                .map(([code]) => code),
        [selectedTemplate]
    );

    // Follows the message's language by default, and the language row carries `direction`
    // — never inferred from the language's name. The toolbar can override it, because a
    // sender writing an English note under a Hebrew template needs to.
    const [dirOverride, setDirOverride] = useState<'auto' | 'rtl' | 'ltr'>('auto');
    const dir: 'rtl' | 'ltr' = useMemo(() => {
        if (dirOverride !== 'auto') return dirOverride;
        const match = languageOptions.find((l) => l.code === languageCode);
        return (match?.direction as 'rtl' | 'ltr') || 'ltr';
    }, [dirOverride, languageOptions, languageCode]);

    // Reset on each open: the dialog is reused across documents, and carrying a previous
    // recipient or message into the next send is the kind of mistake that reaches a customer.
    useEffect(() => {
        if (!open) {
            setPreviewHtml(null);
            return;
        }
        setEmails(defaultEmails || []);
        setSubject(defaultSubject);
        setBody('');
        setAttachments([]);
        setTemplateId(NONE);
        setLanguageCode('');
        setDirOverride('auto');
        setDirty(false);
        setPendingTemplateId(null);
    }, [open]); // eslint-disable-line

    useEffect(() => {
        if (!open) return undefined;

        let cancelled = false;
        // Resolved for this document — the sender depends on it, so asking without the
        // key would display an address the message is not actually sent from.
        const query = documentKey ? `?documentKey=${encodeURIComponent(documentKey)}` : '';
        fetch(`/email-sender${query}`)
            .then((res) => (res.ok ? res.json() : { senderAddress: '' }))
            .then((data) => {
                if (!cancelled)
                    setSender({
                        senderAddress: data?.senderAddress || '',
                        fromName: data?.fromName,
                        replyTo: data?.replyTo
                    });
            })
            .catch(() => {
                if (!cancelled) setSender({ senderAddress: '' });
            });

        return () => {
            cancelled = true;
        };
    }, [open, documentKey]);

    // Templates are resolved server-side through the client's document rules — routing
    // lives on the rule, so this cannot be answered by filtering templates in the browser.
    useEffect(() => {
        if (!open || !documentKey) {
            setTemplates([]);
            setIsTemplatesFallback(false);
            return undefined;
        }

        let cancelled = false;
        fetch(`/api/email-templates/for-document/${encodeURIComponent(documentKey)}`)
            .then((res) => (res.ok ? res.json() : { templates: [] }))
            .then((data) => {
                if (cancelled) return;
                setTemplates(data?.templates || []);
                setIsTemplatesFallback(!!data?.isFallback);
            })
            .catch(() => {
                // No templates is a working state — the sender writes their own message,
                // or sends the attachment alone, exactly as before this dialog existed.
                if (!cancelled) setTemplates([]);
            });

        return () => {
            cancelled = true;
        };
    }, [open, documentKey]);

    const applyTemplate = useCallback(
        (nextId: string, nextLanguage?: string) => {
            setTemplateId(nextId);
            setDirty(false);

            if (nextId === NONE) {
                setSubject(defaultSubject);
                setBody('');
                setLanguageCode('');
                return;
            }

            const template = templates.find((t) => String(t._id) === nextId);
            if (!template) return;

            const available = Object.entries(template.translations || {})
                .filter(([, t]) => (t?.subject || '').trim() || (t?.body || '').trim())
                .map(([code]) => code);
            const code =
                (nextLanguage && available.includes(nextLanguage) && nextLanguage) ||
                (template.defaultLanguage && available.includes(template.defaultLanguage)
                    ? template.defaultLanguage
                    : available[0]) ||
                '';
            const translation = template.translations?.[code] || {};

            setLanguageCode(code);
            // Substitution runs here, in the browser, so the sender reads the finished
            // message and can edit it. An unknown field stays verbatim rather than
            // blanking, which is what makes a typo visible before it is sent.
            setSubject(applyMergeFields(translation.subject, mergeContext) || defaultSubject);
            setBody(applyMergeFields(translation.body, mergeContext));
        },
        [templates, mergeContext, defaultSubject]
    );

    const handleTemplateChange = useCallback(
        (nextId: string) => {
            if (nextId === templateId) return;
            if (dirty) {
                setPendingTemplateId(nextId);
                return;
            }
            applyTemplate(nextId);
        },
        [templateId, dirty, applyTemplate]
    );

    const addFiles = useCallback((fileList: FileList | null) => {
        if (!fileList?.length) return;
        setAttachments((current) => [...current, ...Array.from(fileList)].slice(0, MAX_ATTACHMENT_COUNT));
    }, []);

    const attachmentsBytes = attachments.reduce((sum, file) => sum + file.size, 0);
    const totalBytes = attachmentsBytes + documentSizeBytes;
    const overLimit = totalBytes > MAX_TOTAL_ATTACHMENT_BYTES;

    // Validation is on blur and submit only. An "Invalid e-mail" sitting under a field
    // nobody has touched yet is noise, and it teaches people to ignore the message.
    const noRecipients = !emails.length;
    // A message is required: an email is sent from its preview, and a bare
    // attachment with no text arrived as an unexplained PDF.
    const sendDisabledReason = sending
        ? 'Sending…'
        : noRecipients
          ? 'Add at least one recipient'
          : !hasMessageText(body)
            ? 'Write a message or choose a template'
            : overLimit
              ? `Attachments total ${formatBytes(totalBytes)}, over the ${formatBytes(MAX_TOTAL_ATTACHMENT_BYTES)} limit`
              : '';

    const handlePreview = useCallback(async () => {
        setPreviewLoading(true);
        try {
            const res = await fetch('/email-compose-preview', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ body, language: languageCode || undefined })
            });
            const data = res.ok ? await res.json() : null;
            if (!data) throw new Error(`HTTP ${res.status}`);
            setPreviewHtml(data.html || '');
        } catch (err) {
            console.error('[EmailComposeDialog] preview failed', err);
            displayToast('error', 'Preview failed', 'Could not render the email preview');
        } finally {
            setPreviewLoading(false);
        }
    }, [body, languageCode]);

    const handleSend = useCallback(() => {
        if (sendDisabledReason) return;
        onSend({
            emails,
            subject,
            body,
            language: languageCode || undefined,
            attachments,
            templateName: selectedTemplate?.name
        });
    }, [sendDisabledReason, onSend, emails, subject, body, languageCode, attachments, selectedTemplate]);

    return (
        <DraggableDialog open={open} onClose={onClose} fullWidth maxWidth="md" disableBackdropClick>
            {/* The bar names the dialog, not the document. Repeating the document title
                here made this look like another view of the PDF underneath rather than a
                message being written. The booking context keeps its place as a subtitle,
                where it still tells the sender which file they are writing about. */}
            <ModalFormTitle title="Compose Email" toggleDialog={onClose}>
                {contextHeader && (
                    <Typography variant="caption" color="inherit" sx={{ display: 'block', opacity: 0.85 }}>
                        {contextHeader}
                    </Typography>
                )}
            </ModalFormTitle>

            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} pt={2}>
                    {/* Validates on blur, never on an untouched empty field, and shows
                        its own message inline. */}
                    <EmailChips initEmailChips={defaultEmails} onEmailChipsUpdated={setEmails} />

                    <TextField
                        label="From"
                        size="small"
                        fullWidth
                        value={
                            sender === null
                                ? 'Loading…'
                                : sender.fromName
                                  ? `${sender.fromName} <${sender.senderAddress}>`
                                  : sender.senderAddress
                        }
                        InputProps={{ readOnly: true }}
                        InputLabelProps={{ shrink: true }}
                        // Not editable by design: an address typed here would not be
                        // covered by the sending domain's DKIM signature and would fail
                        // DMARC alignment. What it shows is the sender resolved for this
                        // document, which is what the recipient will actually see.
                        helperText={
                            sender?.senderAddress === ''
                                ? 'No sender is configured — set one in Setup → General → Email Settings'
                                : sender?.replyTo
                                  ? `Replies go to ${sender.replyTo}`
                                  : 'Configured in Email Settings'
                        }
                        error={sender?.senderAddress === ''}
                    />

                    <TextField
                        label="Subject"
                        size="small"
                        fullWidth
                        value={subject}
                        onChange={(e) => {
                            setSubject(e.target.value);
                            setDirty(true);
                        }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ dir: templateId === NONE ? undefined : dir }}
                    />

                    <Box display="flex" gap={2}>
                        <TextField
                            select
                            label="Message template"
                            size="small"
                            fullWidth
                            value={templateId}
                            onChange={(e) => handleTemplateChange(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            helperText={
                                documentKey && !templates.length
                                    ? 'No templates exist yet'
                                    : documentKey && isTemplatesFallback
                                      ? 'No template is linked to this document — showing all templates'
                                      : ' '
                            }
                        >
                            <MenuItem value={NONE}>None</MenuItem>
                            {templates.map((template) => (
                                <MenuItem key={String(template._id)} value={String(template._id)}>
                                    {template.name}
                                </MenuItem>
                            ))}
                        </TextField>

                        {/* Only when the chosen template is written in more than one
                            language — a single-language template has nothing to choose. */}
                        {templateLanguages.length > 1 && (
                            <TextField
                                select
                                label="Language"
                                size="small"
                                value={languageCode}
                                onChange={(e) => applyTemplate(templateId, e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ minWidth: 140 }}
                                helperText=" "
                            >
                                {templateLanguages.map((code) => (
                                    <MenuItem key={code} value={code}>
                                        {code.toUpperCase()}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    </Box>

                    <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Message content
                        </Typography>
                        <MessageEditor
                            value={body}
                            onChange={(html) => {
                                setBody(html);
                                setDirty(true);
                            }}
                            dir={dir}
                            onDirChange={setDirOverride}
                            // Rebuilds the editor when a different template or language
                            // replaces the body. Without it the editor would keep showing
                            // the previous message while the subject changed underneath.
                            contentKey={`${templateId}-${languageCode}`}
                        />
                    </Box>

                    <Box display="flex" flexDirection="column" gap={1}>
                        <Box display="flex" alignItems="center" gap={1.5}>
                            <Button
                                size="small"
                                startIcon={<AttachFileIcon />}
                                onClick={() => fileInputRef.current?.click()}
                                disabled={attachments.length >= MAX_ATTACHMENT_COUNT}
                            >
                                Add attachment
                            </Button>
                            <Typography variant="caption" color={overLimit ? 'error' : 'text.secondary'}>
                                {formatBytes(totalBytes)} of {formatBytes(MAX_TOTAL_ATTACHMENT_BYTES)}
                                {documentSizeBytes ? ' (including this document)' : ''}
                            </Typography>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                hidden
                                onChange={(e) => {
                                    addFiles(e.target.files);
                                    // Cleared so choosing the same file twice still fires onChange.
                                    e.target.value = '';
                                }}
                            />
                        </Box>

                        {attachments.length > 0 && (
                            <Box display="flex" flexWrap="wrap" gap={1}>
                                {attachments.map((file, index) => (
                                    <Chip
                                        key={`${file.name}-${index}`}
                                        size="small"
                                        variant="outlined"
                                        label={`${file.name} · ${formatBytes(file.size)}`}
                                        onDelete={() =>
                                            setAttachments((current) => current.filter((_, i) => i !== index))
                                        }
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>
                </Box>
            </DialogContent>

            <DialogActionsWrapper>
                <Box display="flex" justifyContent="flex-end" gap={1} px={2} py={1} width="100%">
                    <Button variant="outlined" color="secondary" onClick={onClose} disabled={sending}>
                        Cancel
                    </Button>
                    {/* A message is sent from its preview: this opens it, and Send lives
                        there. Disabled, not hidden, with the reason in a tooltip — the
                        span is what lets the tooltip work while the button is disabled. */}
                    <Tooltip title={sendDisabledReason}>
                        <span>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handlePreview}
                                disabled={!!sendDisabledReason || previewLoading}
                            >
                                {sending ? 'Sending…' : previewLoading ? 'Loading…' : 'Preview & Send'}
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </DialogActionsWrapper>

            <Dialog open={previewHtml !== null} onClose={() => setPreviewHtml(null)} maxWidth="md" fullWidth>
                <DialogTitle
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                >
                    <span>{subject || 'Preview'}</span>
                    <IconButton aria-label="Close preview" onClick={() => setPreviewHtml(null)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 1 }}>
                    To: {emails.join(', ')}
                    {attachments.length ? ` · +${attachments.length} attachment(s)` : ''}
                </Typography>
                <DialogContent dividers sx={{ p: 0 }}>
                    {/* sandbox with no permissions: the server has sanitised the body, and
                        the preview has no reason to run scripts or navigate. */}
                    <iframe
                        title="Email preview"
                        sandbox=""
                        srcDoc={previewHtml || ''}
                        style={{ width: '100%', height: '65vh', border: 0, display: 'block' }}
                    />
                </DialogContent>
                <Box display="flex" justifyContent="flex-end" gap={1} px={2} py={1.5}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => setPreviewHtml(null)}
                        disabled={sending}
                    >
                        Back to edit
                    </Button>
                    <Tooltip title={sendDisabledReason}>
                        <span>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSend}
                                disabled={!!sendDisabledReason}
                            >
                                {sending ? 'Sending…' : 'Send'}
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Dialog>

            <WarningConfirmModal
                open={pendingTemplateId !== null}
                toggleModal={() => setPendingTemplateId(null)}
                onConfirm={() => {
                    if (pendingTemplateId !== null) applyTemplate(pendingTemplateId);
                    setPendingTemplateId(null);
                }}
                confirmText="Replace"
                cancelText="Keep my message"
                title="Replace the message?"
                subtitle="Choosing another message will discard the changes you made here."
            />
        </DraggableDialog>
    );
};

export default EmailComposeDialog;
